import React, { useEffect, useState } from "react";
import { Container, Row, Col, Table, Button, Modal } from "react-bootstrap";
import { useSelector } from "react-redux";
import axios from "axios";
import "./Dashboard.scss";

const API_URLS = {
  TESTS: `${process.env.REACT_APP_API_URL}test_case_generation/get_all_test_cases`,
  SCENARIOS:
    `${process.env.REACT_APP_API_URL}test_case_generation/get_all_test_scenarios`,
};

const fetchData = async (url, userId, token, setData) => {
  try {
    const response = await axios.get(`${url}?user_id=${userId}`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    setData(response.data);
  } catch (error) {
    console.error("Error fetching data:", error);
  }
};

const TestTable = ({ title, columns, data, onRowClick }) => (
  <Col md={6} className="test-section">
    <div className="test-table-container">
      <h5 className="test-table-title">{title}</h5>
      <div className="test-table-scrollable">
        <Table striped bordered hover>
          <thead className="test-table-header">
            {title === "Test Documents" ? (
              <tr>
                <th>User Story ID</th>
                <th>User Story Title</th>
                <th>User Story Description</th>
                <th>Acceptance Criteria</th>
                <th>Scenario</th>
                <th>Method</th>
                <th>Created At</th>
              </tr>
            ) : (
              <tr>
                <th>User Story ID</th>
                <th>User Story Title</th>
                <th>User Story Description</th>
                <th>Method</th>
                <th>Created At</th>
              </tr>
            )}
          </thead>
          <tbody>
            {data?.length > 0 ? (
              data?.map((row, index) => (
                <tr key={index} onClick={() => onRowClick(row)}>
                  {columns.map((col, colIndex) => (
                    <td key={colIndex}>
                      {Array.isArray(row[col]) ? row[col].join(", ") : row[col]}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} className="test-table-no-data">
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </div>
  </Col>
);

const DetailsModal = ({ show, onHide, data }) => {
  const testCaseHeaders = [
    "Test Case ID",
    "Test Case Title",
    "Test Case Description",
    "Test Steps",
    "Test Data",
    "Expected Result",
  ];

  const testCaseHeadersForAPI = [
    "Test Case ID",
    "Test Case Title",
    "Test Case Description",
    "API Endpoint",
    "Request Method",
    "Request Payload",
    "Test Steps",
    "Expected Result",
  ];

  const getHeaders = () => {
    if (!data || data?.length === 0) return [];
    const firstItem = data[0];
    const keys = Object.keys(firstItem);

    if (keys.includes("API Endpoint")) {
      return testCaseHeadersForAPI;
    } else if (testCaseHeaders?.every((key) => key in firstItem)) {
      return testCaseHeaders;
    } else {
      return keys;
    }
  };

  const headers = getHeaders();

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {data && data?.length > 0 ? (
          <div className="details-modal-scrollable">
            <table className="table table-bordered">
              <thead className="details-modal-header">
                <tr>
                  {headers?.map((header, idx) => (
                    <th
                      key={idx}
                      className={`border px-2 py-2 ${
                        header === "Test Steps" ? "details-modal-wide-column" : ""
                      }`}
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data?.map((item, rowIndex) => (
                  <tr key={rowIndex}>
                    {headers?.map((header, colIndex) => {
                      const cellData = item[header];

                      return (
                        <td key={colIndex} className="border px-2 py-2">
                          {typeof cellData === "string" ? (
                            <pre className="details-modal-pre">
                              {cellData}
                            </pre>
                          ) : Array.isArray(cellData) ? (
                            cellData.join(", ")
                          ) : typeof cellData === "object" &&
                            cellData !== null ? (
                            <pre className="details-modal-pre">
                              {JSON.stringify(cellData, null, 2)}
                            </pre>
                          ) : (
                            cellData?.toString() || ""
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p>No data available</p>
        )}
      </Modal.Body>
    </Modal>
  );
};

const RecentTests = () => {
  const [testCases, setTestCases] = useState(null);
  const [testScenarios, setTestScenarios] = useState(null);
  const [selectedTestCase, setSelectedTestCase] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const IdToken = localStorage.getItem("msalIdToken");
  const userData = useSelector((state) => state.auth.userData);
  const userId = userData?.mail;

  const GET_TESTCASE_BY_ID =
    `${process.env.REACT_APP_API_URL}test_case_generation/view_test_case`;
  const GET_TESTSCENARIO_BY_ID =
    `${process.env.REACT_APP_API_URL}test_case_generation/view_test_scenario`;
  const getTestCaseById = async (data, url) => {
    try {
      const response = await axios.get(`${url}?doc_id=${data.doc_id}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${IdToken}`,
        },
      });
      const res = response?.data;
      setSelectedTestCase(res);
    } catch (err) {
      console.log(err);
    }
  };
  useEffect(() => {
    if (userId && IdToken) {
      if (!testCases) {
        fetchData(API_URLS.TESTS, userId, IdToken, setTestCases);
      }
      if (!testScenarios) {
        fetchData(API_URLS.SCENARIOS, userId, IdToken, setTestScenarios);
      }
    }
  }, [userId, IdToken]);
  return (
    <Container fluid className="recent-tests p-2">
      <Row>
        <TestTable
          title="Test Documents"
          columns={[
            "user_story_id",
            "user_story_title",
            "user_story_description",
            "acceptance_criteria",
            "scenario",
            "method",
            "created_at",
          ]}
          data={testCases?.test_cases}
          onRowClick={(row) => {
            getTestCaseById(row, GET_TESTCASE_BY_ID);
            setSelectedTestCase(row);
            setShowModal(true);
          }}
        />
        <TestTable
          title="Test Scenarios"
          columns={[
            "user_story_id",
            "user_story_title",
            "user_story_description",
            "method",
            "created_at",
          ]}
          data={testScenarios?.test_scenarios}
          onRowClick={(row) => {
            getTestCaseById(row, GET_TESTSCENARIO_BY_ID);
            setSelectedTestCase(row);
            setShowModal(true);
          }}
        />
      </Row>
      <DetailsModal
        show={showModal}
        onHide={() => setShowModal(false)}
        data={selectedTestCase?.test_case}
      />
    </Container>
  );
};

export default RecentTests;